import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Landmark, CalendarDays, MapPin, Clock, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";

const OPTIONS = [
  { v: "attending", l: "Saya akan Hadir", en: "I will Attend", c: "border-positive text-positive bg-positive-bg" },
  { v: "tentative", l: "Mungkin / Belum Pasti", en: "Tentative", c: "border-brass text-brass bg-brass/10" },
  { v: "not_attending", l: "Tidak Dapat Hadir", en: "Cannot Attend", c: "border-brick text-brick bg-brick-bg" },
];

export default function PublicRsvp() {
  const { token } = useParams();
  const [meeting, setMeeting] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [step, setStep] = useState("verify"); // verify | form | done
  const [name, setName] = useState("");
  const [ic, setIc] = useState("");
  const [member, setMember] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState("attending");
  const [mode, setMode] = useState("Physical");
  const [proxy, setProxy] = useState("");

  useEffect(() => {
    api.get(`/public/rsvp/${token}`).then((r) => setMeeting(r.data.meeting)).catch(() => setNotFound(true));
  }, [token]);

  const verify = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const r = await api.post(`/public/rsvp/${token}/verify`, { name: name.trim(), ic: ic.trim() });
      setMember(r.data);
      if (r.data.existing) {
        setResponse(r.data.existing.response);
        if (r.data.existing.mode) setMode(r.data.existing.mode === "online" ? "Online" : "Physical");
        setProxy(r.data.existing.proxy_name || "");
      }
      setStep("form");
    } catch (err) {
      setError(apiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post(`/public/rsvp/${token}/respond`, {
        name: name.trim(), ic: ic.trim(),
        response, mode: response === "attending" ? mode.toLowerCase() : null,
        proxy_name: response === "not_attending" ? proxy.trim() : null,
      });
      setStep("done");
    } catch (err) { setError(apiError(err.response?.data?.detail)); } finally { setLoading(false); }
  };

  if (notFound) return <Shell><p className="text-brick text-center py-8">Link RSVP tidak sah. / Invalid RSVP link.</p></Shell>;
  if (!meeting) return <Shell><div className="flex justify-center py-8"><Loader2 className="animate-spin text-brass" size={26} /></div></Shell>;

  return (
    <Shell>
      <div className="mb-6">
        <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-wider bg-ink text-paper mb-2">{meeting.meeting_type}</span>
        <h1 className="font-serif text-2xl text-ink">{meeting.title}</h1>
        <div className="mt-3 space-y-1.5 text-sm text-slate">
          <div className="flex items-center gap-2"><CalendarDays size={15} className="text-brass" /> {meeting.date}</div>
          <div className="flex items-center gap-2"><Clock size={15} className="text-brass" /> {meeting.start_time}–{meeting.end_time}</div>
          <div className="flex items-center gap-2"><MapPin size={15} className="text-brass" /> {meeting.venue} · {meeting.modes?.join(" / ")}</div>
        </div>
      </div>

      {error && <div className="mb-4 bg-brick-bg text-brick border border-brick/20 px-3 py-2 rounded-sm text-sm" data-testid="rsvp-error">{error}</div>}

      {step === "verify" && (
        <form onSubmit={verify} className="space-y-4">
          <div className="flex items-center gap-2 text-xs text-slate bg-brass/5 border border-brass/20 rounded-sm px-3 py-2">
            <ShieldCheck size={15} className="text-brass" /> Sahkan diri anda / Verify yourself to RSVP
          </div>
          <div><label className="block text-xs font-medium text-slate mb-1">Nama Penuh / Full Name</label>
            <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Nama seperti dalam rekod" data-testid="rsvp-name" className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2.5 text-sm outline-none focus:border-brass text-ink" /></div>
          <div><label className="block text-xs font-medium text-slate mb-1">No. Kad Pengenalan / IC Number</label>
            <input required value={ic} onChange={(e) => setIc(e.target.value)} placeholder="900101-14-5533" data-testid="rsvp-ic" className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2.5 text-sm font-mono outline-none focus:border-brass text-ink" /></div>
          <button type="submit" disabled={loading} data-testid="rsvp-verify-btn" className="w-full bg-ink text-white hover:bg-ink-light py-2.5 rounded-sm text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">{loading && <Loader2 size={16} className="animate-spin" />} Teruskan / Continue</button>
        </form>
      )}

      {step === "form" && member && (
        <form onSubmit={submit} className="space-y-5">
          <p className="text-sm text-ink">Salam <strong>{member.name}</strong> ({member.membership_no})</p>
          <div className="space-y-2">
            <label className="block text-xs font-medium text-slate uppercase tracking-wider">Jawapan Anda / Your Response</label>
            {OPTIONS.map((o) => (
              <button type="button" key={o.v} onClick={() => setResponse(o.v)} data-testid={`rsvp-opt-${o.v}`}
                className={`w-full text-left border-2 rounded-sm px-4 py-3 transition-colors ${response === o.v ? o.c : "border-hairline text-slate hover:border-slate/40"}`}>
                <div className="font-medium text-sm">{o.l}</div><div className="text-xs opacity-70">{o.en}</div>
              </button>
            ))}
          </div>

          {response === "attending" && meeting.modes?.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-slate uppercase tracking-wider mb-2">Mod Kehadiran / Attendance Mode</label>
              <div className="flex gap-2">
                {meeting.modes.map((mo) => (
                  <button type="button" key={mo} onClick={() => setMode(mo)} data-testid={`rsvp-mode-${mo}`}
                    className={`flex-1 border-2 rounded-sm py-2.5 text-sm transition-colors ${mode === mo ? "border-ink bg-ink text-paper" : "border-hairline text-slate hover:border-slate/40"}`}>{mo}</button>
                ))}
              </div>
            </div>
          )}

          {response === "not_attending" && (
            <div>
              <label className="block text-xs font-medium text-slate mb-1">Nama Proksi (jika ada) / Proxy Name (optional)</label>
              <input value={proxy} onChange={(e) => setProxy(e.target.value)} data-testid="rsvp-proxy" className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2.5 text-sm focus:border-brass focus:ring-1 focus:ring-brass outline-none text-ink" />
            </div>
          )}

          <button type="submit" disabled={loading} data-testid="rsvp-submit-btn" className="w-full bg-brass text-ink font-semibold hover:bg-brass-hover py-2.5 rounded-sm text-sm flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">{loading && <Loader2 size={16} className="animate-spin" />} Hantar RSVP / Submit</button>
        </form>
      )}

      {step === "done" && (
        <div className="text-center py-8" data-testid="rsvp-done">
          <CheckCircle2 size={48} className="text-positive mx-auto mb-4" />
          <h2 className="font-serif text-2xl text-ink">Terima kasih!</h2>
          <p className="text-sm text-slate mt-2">Jawapan RSVP anda telah direkodkan.<br />Your RSVP has been recorded.</p>
        </div>
      )}
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-ink flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-5">
          <Landmark className="text-brass" strokeWidth={1.5} size={26} />
          <span className="font-serif text-xl text-paper">KKPKLB</span>
        </div>
        <div className="bg-white rounded-md shadow-2xl p-7">{children}</div>
        <p className="text-center text-paper/40 text-xs mt-4">Koperasi Kumpulan Profesional Kuala Lumpur Berhad</p>
      </div>
    </div>
  );
}
